import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import CatholicEmojiPicker, { renderTextWithCatholicEmojis, CATHOLIC_EMOJIS } from "@/components/CatholicEmojiPicker";
import { SocialEmbeds } from "@/components/SocialEmbed";
import LikesDialog from "@/components/LikesDialog";

// Helper component to render text with Catholic emojis
export function RenderContent({ content }) {
  if (!content) return null;
  
  // Auto-link bare URLs: https://..., http://..., www....
  const autoLinkUrls = (text) => {
    if (typeof text !== 'string') return [text];
    const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.substring(lastIndex, match.index));
      const url = match[0].startsWith('www.') ? `https://${match[0]}` : match[0];
      // Strip trailing punctuation that's likely not part of the URL
      let display = match[0].replace(/[.,;:!?)]+$/, '');
      let href = url.replace(/[.,;:!?)]+$/, '');
      const trailingChars = match[0].substring(display.length);
      parts.push(
        <a key={`url-${match.index}`} href={href} target="_blank" rel="noopener noreferrer"
          className="text-[var(--liturgical-primary)] hover:underline break-all">{display}</a>
      );
      if (trailingChars) parts.push(trailingChars);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(text.substring(lastIndex));
    return parts.length > 0 ? parts : [text];
  };

  // Function to convert markdown links to JSX
  const parseMarkdownLinks = (text) => {
    // Match markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add the link as JSX
      parts.push(
        <a 
          key={match.index} 
          href={match[2]} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {match[1]}
        </a>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };

  // Parse @mentions: @Name_With_Underscores
  const parseMentions = (text) => {
    if (typeof text !== 'string') return text;
    
    const parts = [];
    const regex = /@([a-zA-Z0-9_]+)/g;
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before the mention
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add the mention as a styled span
      const displayName = match[1].replace(/_/g, ' ');
      parts.push(
        <span 
          key={match.index}
          className="text-[var(--liturgical-primary)] font-semibold"
          title={`@${displayName}`}
        >
          @{displayName}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };
  
  // Apply all parsers to a text string: markdown links → bare URLs → mentions
  const parseAll = (text) => {
    const linkParts = parseMarkdownLinks(text);
    const result = [];
    for (let i = 0; i < linkParts.length; i++) {
      const part = linkParts[i];
      if (typeof part !== 'string') { result.push(part); continue; }
      const urlParts = autoLinkUrls(part);
      for (let j = 0; j < urlParts.length; j++) {
        const uPart = urlParts[j];
        if (typeof uPart !== 'string') { result.push(uPart); continue; }
        const mentionParts = parseMentions(uPart);
        for (const mPart of mentionParts) result.push(mPart);
      }
    }
    return result;
  };

  // Check if content has Catholic emoji markers
  if (content.includes("[catholic:")) {
    const catholicEmojiMap = {};
    CATHOLIC_EMOJIS.forEach(e => {
      catholicEmojiMap[e.id] = e;
    });
    
    const parts = content.split(/(\[catholic:\w+\])/g);
    return (
      <>
        {parts.map((part, i) => {
          const match = part.match(/\[catholic:(\w+)\]/);
          if (match) {
            const emoji = catholicEmojiMap[match[1]];
            if (emoji) {
              return <img key={i} src={emoji.url} alt={emoji.name} className="inline-block w-5 h-5 align-text-bottom" />;
            }
          }
          return <span key={i}>{parseAll(part)}</span>;
        })}
      </>
    );
  }
  
  return <>{parseAll(content)}</>;
}
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Heart, MessageCircle, Send, ImagePlus, Globe, Church, MapPin, Megaphone, X, Bookmark, Flag, Users, Home, Sparkles, Star, Phone, Mail, ExternalLink, Building2, MoreHorizontal, VolumeX, AlertTriangle, BarChart3, Plus, Trash2, Pencil, FolderPlus, Check, Loader2, Video, Upload, MessageSquareHeart, Rocket, CalendarDays, Clock, Ban, Smile, Repeat2, Share2, Image as ImageIcon, Reply, ThumbsUp, ChevronDown, ChevronUp } from "lucide-react";
import { getPosts, createPost, updatePost, deletePost, likePost, getComments, createComment, likeComment, deleteComment, repostPost, getParishes, requestMembership, uploadImage, resolveImageUrl, toggleBookmark, reportContent, muteAuthor, reportPost, recordPostView, getUserParishCount, getBookmarkFolders, createBookmarkFolder, getVideoStatus, getEvents, blockUser, detectEmbeds, sendMessage, searchUsers, getMyBusinessListings } from "@/lib/api";
import { toast } from "sonner";
import { FriendSuggestionsWidget } from "@/components/FriendSuggestionsWidget";
import PollCard from "@/components/PollCard";
import VideoProcessingIndicator from "@/components/posts/VideoProcessingIndicator";
import CloudflareVideoPlayer from "@/components/posts/CloudflareVideoPlayer";
import useCloudflareUpload from "@/hooks/useCloudflareUpload";

// CommentInput component with emoji, image support, and auto-expanding textarea
export function CommentInput({ postId, onCommentAdded, parentId = null, placeholder, autoFocus = false, onCancel }) {
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [searchingMentions, setSearchingMentions] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const mentionRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) setShowEmojiPicker(false);
      if (mentionRef.current && !mentionRef.current.contains(e.target)) setShowMentions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (autoFocus && textareaRef.current) textareaRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (mentionSearch.length < 1) { setMentionResults([]); return; }
    const t = setTimeout(async () => {
      setSearchingMentions(true);
      try {
        const res = await searchUsers(mentionSearch, { limit: 5 });
        setMentionResults(res.data?.users || res.data || []);
      } catch {}
      setSearchingMentions(false);
    }, 200);
    return () => clearTimeout(t);
  }, [mentionSearch]);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  };

  const handleContentChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setContent(value);
    setCursorPosition(cursor);
    autoResize(e.target);
    const textBeforeCursor = value.substring(0, cursor);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) { setShowMentions(true); setMentionSearch(mentionMatch[1]); }
    else { setShowMentions(false); setMentionSearch(""); }
  };

  const insertMention = (user) => {
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);
    const mentionStart = textBeforeCursor.lastIndexOf("@");
    const beforeMention = content.substring(0, mentionStart);
    const mentionText = `@${user.name.replace(/\s+/g, "_")}`;
    const newContent = beforeMention + mentionText + " " + textAfterCursor;
    setContent(newContent);
    setShowMentions(false);
    setMentionSearch("");
    if (!mentionedUsers.find(m => m.user_id === user.user_id)) {
      setMentionedUsers([...mentionedUsers, { user_id: user.user_id, name: user.name }]);
    }
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be less than 5MB"); return; }
    setUploading(true);
    try {
      const res = await uploadImage(file);
      const url = res.data?.url || res.data?.data?.url;
      if (url) setImageUrl(url);
    } catch { toast.error("Failed to upload image"); }
    setUploading(false);
  };

  const handleEmojiClick = (emojiData) => {
    if (emojiData.emoji) setContent(prev => prev + emojiData.emoji);
  };

  const handleSubmit = async () => {
    if (!content.trim() && !imageUrl) return;
    setSubmitting(true);
    try {
      const res = await createComment(postId, {
        content: content.trim(),
        image_url: imageUrl,
        mentioned_users: mentionedUsers,
        parent_id: parentId,
      });
      onCommentAdded(res.data);
      setContent("");
      setImageUrl(null);
      setMentionedUsers([]);
      if (textareaRef.current) { textareaRef.current.style.height = "auto"; }
      toast.success(parentId ? "Reply added" : "Comment added");
    } catch { toast.error("Failed to add comment"); }
    setSubmitting(false);
  };

  const handleKeyDown = (e) => {
    if (showMentions && e.key === "Escape") { e.preventDefault(); setShowMentions(false); return; }
    // Ctrl/Cmd+Enter to submit, plain Enter = newline
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-2">
      {imageUrl && (
        <div className="relative inline-block">
          <img src={imageUrl} alt="Upload preview" className="max-w-[150px] rounded-md border border-[#E5E0D5]" />
          <button onClick={() => setImageUrl(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600">×</button>
        </div>
      )}
      <div className="flex gap-2 items-end">
        <div className="flex-1 relative">
          <div className="flex gap-1 items-end border border-[#E5E0D5] rounded-md bg-white px-2">
            <div className="flex items-center gap-1 pb-2 pt-1">
              <div className="relative" ref={emojiPickerRef}>
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-1 text-[#6B665E] hover:text-[var(--liturgical-primary)]" title="Add emoji"
                  data-testid={`comment-emoji-btn-${postId}`}><Smile className="w-4 h-4" /></button>
                {showEmojiPicker && (
                  <div className="absolute bottom-8 left-0 z-50">
                    <CatholicEmojiPicker onEmojiClick={handleEmojiClick} width={280} height={300} />
                  </div>
                )}
              </div>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="p-1 text-[#6B665E] hover:text-[var(--liturgical-primary)]" title="Add image">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleContentChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || (parentId ? "Write a reply..." : "Write a comment...")}
              rows={1}
              className="flex-1 text-xs py-2 bg-transparent font-['Nunito'] focus:outline-none resize-none min-h-[32px] max-h-[200px] overflow-y-auto"
              data-testid={parentId ? `reply-input-${postId}` : `comment-input-${postId}`}
            />
          </div>
          {showMentions && (
            <div ref={mentionRef} className="absolute bottom-full left-0 mb-1 w-full bg-white border border-[#E5E0D5] rounded-md shadow-lg z-50 max-h-[200px] overflow-y-auto">
              {searchingMentions ? (
                <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[var(--liturgical-primary)]" /></div>
              ) : mentionResults.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#6B665E] font-['Nunito']">{mentionSearch.length === 0 ? "Type a name..." : "No users found"}</div>
              ) : (
                mentionResults.map((user) => (
                  <button key={user.user_id} onClick={() => insertMention(user)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F9F7F2] text-left transition-colors">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={user.picture || user.profile_picture} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="font-['Nunito'] text-xs text-[#2D2A26]">{user.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
          <p className="text-[10px] text-[#9C9689] font-['Nunito'] mt-0.5 ml-1">Ctrl+Enter to send</p>
        </div>
        <Button size="sm" onClick={handleSubmit} disabled={submitting || (!content.trim() && !imageUrl)}
          className="bg-[var(--liturgical-primary)] hover:brightness-90 h-8 px-3 mb-4"
          data-testid={parentId ? `reply-submit-${postId}` : `comment-submit-${postId}`}>
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 px-2 mb-4 text-[#6B665E]">
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Builds a threaded comment tree from flat list and renders it
function CommentThread({ comments, postId, currentUserId, onCommentsChange }) {
  // Build tree: top-level comments + their nested replies
  const topLevel = comments.filter(c => !c.parent_id);
  const byParent = {};
  for (const c of comments) {
    if (c.parent_id) {
      if (!byParent[c.parent_id]) byParent[c.parent_id] = [];
      byParent[c.parent_id].push(c);
    }
  }

  const handleLike = async (commentId) => {
    try {
      const res = await likeComment(postId, commentId);
      onCommentsChange(prev => prev.map(c =>
        c.comment_id === commentId ? { ...c, user_liked: res.data.liked, like_count: res.data.like_count } : c
      ));
    } catch { toast.error("Failed to like"); }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteComment(postId, commentId);
      onCommentsChange(prev => prev.filter(c => c.comment_id !== commentId && c.parent_id !== commentId));
      toast.success("Comment deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleReplyAdded = (newReply) => {
    onCommentsChange(prev => [...prev, newReply]);
  };

  const renderComment = (comment, depth = 0) => {
    const replies = byParent[comment.comment_id] || [];
    const maxIndent = 3;
    const indent = Math.min(depth, maxIndent);

    return (
      <div key={comment.comment_id} style={{ marginLeft: indent > 0 ? `${indent * 24}px` : 0 }}>
        <CommentItem
          comment={comment}
          postId={postId}
          currentUserId={currentUserId}
          onLike={handleLike}
          onDelete={handleDelete}
          onReplyAdded={handleReplyAdded}
          depth={depth}
        />
        {replies.map(r => renderComment(r, depth + 1))}
      </div>
    );
  };

  return <div className="space-y-1">{topLevel.map(c => renderComment(c, 0))}</div>;
}

function CommentItem({ comment: c, postId, currentUserId, onLike, onDelete, onReplyAdded, depth }) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const navigate = useNavigate();
  const isAuthor = c.author_id === currentUserId;
  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="flex gap-2 py-1.5" data-testid={`comment-${c.comment_id}`}>
      <Avatar className="h-7 w-7 border border-[#E5E0D5] flex-shrink-0 cursor-pointer"
        onClick={() => navigate(`/profile/${c.author_id}`)}>
        <AvatarImage src={c.author_picture || c.author?.picture} />
        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">
          {(c.author_name || c.author?.name)?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="bg-[#F9F7F2] rounded-lg px-3 py-2">
          <span className="text-xs font-semibold text-[#2D2A26] font-['Nunito'] cursor-pointer hover:underline"
            onClick={() => navigate(`/profile/${c.author_id}`)}>
            {c.author_name || c.author?.name || "User"}
          </span>
          <div className="text-xs text-[#2D2A26] font-['Nunito'] mt-0.5 whitespace-pre-line">
            <RenderContent content={c.content} />
          </div>
          {c.image_url && (
            <img src={c.image_url} alt="Attachment" className="mt-2 max-w-[200px] rounded-md border border-[#E5E0D5]" />
          )}
          {c.media_urls && c.media_urls.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {c.media_urls.map((url, idx) => (
                <img key={idx} src={url} alt={`Attachment ${idx + 1}`} className="max-w-[150px] rounded-md border border-[#E5E0D5]" />
              ))}
            </div>
          )}
        </div>
        {/* Actions row */}
        <div className="flex items-center gap-3 mt-1 ml-1">
          <button onClick={() => onLike(c.comment_id)}
            className={`flex items-center gap-1 text-[10px] font-['Nunito'] font-semibold transition-colors ${c.user_liked ? "text-[var(--liturgical-primary)]" : "text-[#9C9689] hover:text-[var(--liturgical-primary)]"}`}
            data-testid={`like-comment-${c.comment_id}`}>
            <Heart className={`w-3 h-3 ${c.user_liked ? "fill-current" : ""}`} />
            {c.like_count > 0 && c.like_count}
          </button>
          {depth < 3 && (
            <button onClick={() => setShowReplyInput(!showReplyInput)}
              className="flex items-center gap-1 text-[10px] font-['Nunito'] font-semibold text-[#9C9689] hover:text-[var(--liturgical-primary)] transition-colors"
              data-testid={`reply-btn-${c.comment_id}`}>
              <Reply className="w-3 h-3" />Reply
            </button>
          )}
          <span className="text-[10px] text-[#9C9689] font-['Nunito']">{timeAgo(c.created_at)}</span>
          {isAuthor && (
            <button onClick={() => onDelete(c.comment_id)}
              className="text-[10px] font-['Nunito'] font-semibold text-[#9C9689] hover:text-red-500 transition-colors"
              data-testid={`delete-comment-${c.comment_id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        {/* Inline reply input */}
        {showReplyInput && (
          <div className="mt-2">
            <CommentInput
              postId={postId}
              parentId={c.comment_id}
              onCommentAdded={(reply) => { onReplyAdded(reply); setShowReplyInput(false); }}
              autoFocus
              onCancel={() => setShowReplyInput(false)}
              placeholder={`Reply to ${c.author_name || "User"}...`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function YouTubeTitle({ videoId }) {
  const [title, setTitle] = useState(null);
  useEffect(() => {
    fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`)
      .then(r => r.json())
      .then(data => { if (data.title) setTitle(data.title); })
      .catch(() => {});
  }, [videoId]);
  if (!title) return null;
  return (
    <p className="mt-1.5 text-xs sm:text-sm font-['Nunito'] font-medium text-[#2D2A26] leading-snug line-clamp-2" data-testid={`yt-title-${videoId}`}>
      {title}
    </p>
  );
}


export function PostCard({ post, onLike, onComment, currentUserId, onMuteAuthor, onBlockUser, onEdit, onDelete, defaultShowComments }) {
  const [showComments, setShowComments] = useState(!!defaultShowComments);
  const [comments, setComments] = useState(post.comments || []);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const [muting, setMuting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [editing, setEditing] = useState(false);
  const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [viewed, setViewed] = useState(false);
  const [repostDialogOpen, setRepostDialogOpen] = useState(false);
  const [repostComment, setRepostComment] = useState("");
  const [reposting, setReposting] = useState(false);
  const [localRepostCount, setLocalRepostCount] = useState(post.repost_count || 0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareRecipient, setShareRecipient] = useState("");
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [likesDialogOpen, setLikesDialogOpen] = useState(false);
  const [selectedShareUser, setSelectedShareUser] = useState(null);
  const [sharing, setSharing] = useState(false);
  const navigate = useNavigate();
  
  const isOwnPost = post.author_id === currentUserId;
  const isNewsPost = post.is_vatican_news || post.is_ewtn_news || post.is_saint_of_day;

  // Extract YouTube video ID from various URL formats
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/ // Just the video ID
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Find all YouTube URLs in content and return video IDs with type info
  const findYouTubeLinks = (content) => {
    if (!content) return [];
    const urlPattern = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S*)?/gi;
    const matches = [...content.matchAll(urlPattern)];
    const seen = new Set();
    const results = [];
    for (const m of matches) {
      if (seen.has(m[1])) continue;
      seen.add(m[1]);
      const isShort = /youtube\.com\/shorts\//i.test(m[0]);
      results.push({ id: m[1], isShort });
    }
    return results;
  };

  // Get YouTube URLs from content
  const youtubeVideos = findYouTubeLinks(post.content);

  // Strip embedded URLs from visible content to avoid showing them twice
  const getCleanContent = () => {
    let cleaned = post.content || "";
    // Strip YouTube URLs if they will be embedded
    if (youtubeVideos.length > 0) {
      cleaned = cleaned.replace(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}(?:\S*)?/gi, '');
    }
    // Strip TikTok URLs (all formats: @user/video, vm.tiktok.com, tiktok.com/t/)
    const hasTikTokEmbed = post.embeds?.some(e => e.platform === 'tiktok');
    const tiktokUrlPattern = /https?:\/\/(?:(?:www|vm)\.)?tiktok\.com\/[^\s]+/gi;
    if (hasTikTokEmbed || cleaned.match(tiktokUrlPattern)) {
      cleaned = cleaned.replace(tiktokUrlPattern, '');
    }
    // Strip Instagram URLs (all formats: /p/, /reel/)
    const hasInstaEmbed = post.embeds?.some(e => e.platform === 'instagram');
    const instaUrlPattern = /https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/[^\s]+/gi;
    if (hasInstaEmbed || cleaned.match(instaUrlPattern)) {
      cleaned = cleaned.replace(instaUrlPattern, '');
    }
    return cleaned.trim();
  };

  const displayContent = getCleanContent();

  // Record view when post comes into viewport
  useEffect(() => {
    if (!viewed && post.post_id) {
      // Record view after a short delay (user actually saw the post)
      const timer = setTimeout(async () => {
        try {
          await recordPostView(post.post_id);
          setViewed(true);
        } catch {}
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [post.post_id, viewed]);

  // Ordo Amoris priority badge config
  const getPriorityBadge = () => {
    if (!post.priority_label) return null;
    
    const config = {
      "Family": { icon: Home, color: "bg-[var(--liturgical-secondary)] text-white", emoji: "👨‍👩‍👧" },
      "Friend": { icon: Users, color: "bg-[var(--liturgical-primary)] text-white", emoji: "👥" },
      "Parish": { icon: Church, color: "bg-[var(--liturgical-primary)] text-white", emoji: "⛪" },
      "Diocese": { icon: Church, color: "bg-[#6B665E] text-white", emoji: "✝️" }
    };
    
    const badge = config[post.priority_label];
    if (!badge) return null;
    
    const Icon = badge.icon;
    return (
      <Badge 
        className={`${badge.color} text-[10px] font-['Nunito'] px-1.5 py-0 h-5`}
        data-testid={`priority-badge-${post.priority_type}`}
      >
        <Icon className="w-2.5 h-2.5 mr-0.5" /> {post.priority_label}
      </Badge>
    );
  };

  const loadComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      try {
        const res = await getComments(post.post_id);
        setComments(res.data);
      } catch {}
      setLoadingComments(false);
    }
    setShowComments(!showComments);
  };

  // Auto-load comments when defaultShowComments is true
  useEffect(() => {
    if (defaultShowComments && comments.length === 0) {
      setLoadingComments(true);
      getComments(post.post_id).then(res => setComments(res.data)).catch(() => {}).finally(() => setLoadingComments(false));
    }
  }, []);

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await createComment(post.post_id, { content: newComment });
      setComments([...comments, res.data]);
      setNewComment("");
      onComment();
    } catch {
      toast.error("Failed to post comment");
    }
  };

  const handleBookmark = async (folderId = null) => {
    try {
      const res = await toggleBookmark({ content_type: "post", content_id: post.post_id, folder_id: folderId });
      setBookmarked(res.data.bookmarked);
      setBookmarkDialogOpen(false);
      if (res.data.bookmarked) {
        const folderName = folderId ? folders.find(f => f.folder_id === folderId)?.name : null;
        toast.success(folderName ? `Saved to "${folderName}"` : "Saved to bookmarks");
      } else {
        toast.success("Removed from bookmarks");
      }
    } catch {
      toast.error("Failed to bookmark");
    }
  };

  const openBookmarkDialog = async () => {
    setLoadingFolders(true);
    try {
      const res = await getBookmarkFolders();
      setFolders(res.data.folders || []);
    } catch {}
    setLoadingFolders(false);
    setBookmarkDialogOpen(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const res = await createBookmarkFolder(newFolderName.trim());
      setFolders([...folders, res.data]);
      setSelectedFolderId(res.data.folder_id);
      setNewFolderName("");
      toast.success("Folder created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create folder");
    }
    setCreatingFolder(false);
  };

  const handleEdit = async () => {
    if (!editContent.trim()) {
      toast.error("Post cannot be empty");
      return;
    }
    setEditing(true);
    try {
      await onEdit(post.post_id, editContent.trim());
      setEditDialogOpen(false);
      toast.success("Post updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update post");
    }
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(post.post_id);
      setDeleteDialogOpen(false);
      toast.success("Post deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete post");
    }
    setDeleting(false);
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error("Please select a reason for reporting");
      return;
    }
    setReporting(true);
    try {
      await reportPost(post.post_id, reportReason);
      toast.success("Report submitted. Thank you for helping keep our community safe.");
      setReportDialogOpen(false);
      setReportReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to report post");
    }
    setReporting(false);
  };

  const handleMuteAuthor = async () => {
    setMuting(true);
    try {
      await muteAuthor(post.author_id);
      toast.success(`Muted ${post.author_name}. Their posts will appear less often in your feed.`);
      if (onMuteAuthor) onMuteAuthor(post.author_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mute author");
    }
    setMuting(false);
  };

  const handleBlockUser = async () => {
    setBlocking(true);
    try {
      await blockUser(post.author_id);
      toast.success(`Blocked ${post.author_name}. You won't see their posts anymore.`);
      if (onBlockUser) onBlockUser(post.author_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to block user");
    }
    setBlocking(false);
  };

  const handleRepost = async () => {
    setReposting(true);
    try {
      await repostPost(post.post_id, { comment: repostComment.trim() || null });
      setLocalRepostCount(prev => prev + 1);
      toast.success("Post shared to your feed!");
      setRepostDialogOpen(false);
      setRepostComment("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to repost");
    }
    setReposting(false);
  };

  const searchUsersForShare = async (query) => {
    if (query.length < 2) {
      setShareSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await searchUsers(query, { limit: 5 });
      const users = res.data?.users || res.data || [];
      setShareSearchResults(users.filter(u => u.user_id !== currentUserId));
    } catch (err) {
      console.error("Search failed:", err);
    }
    setSearchingUsers(false);
  };

  const handleShareToUser = async () => {
    if (!selectedShareUser) {
      toast.error("Please select a user to share with");
      return;
    }
    setSharing(true);
    try {
      // Create a message with the post link
      const postUrl = `${window.location.origin}/post/${post.post_id}`;
      const messageContent = `Check out this post: ${postUrl}\n\n"${post.content?.substring(0, 100)}${post.content?.length > 100 ? '...' : ''}"`;
      
      await sendMessage({ 
        receiver_id: selectedShareUser.user_id, 
        content: messageContent 
      });
      toast.success(`Post shared with ${selectedShareUser.name}`);
      setShareDialogOpen(false);
      setSelectedShareUser(null);
      setShareRecipient("");
      setShareSearchResults([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to share post");
    }
    setSharing(false);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Handle author click - Vatican News should go to /vatican-news, others to profile
  const handleAuthorClick = () => {
    if (post.is_vatican_news) {
      navigate('/vatican-news');
    } else if (post.is_ewtn_news) {
      navigate('/ewtn-news');
    } else if (post.is_saint_of_day) {
      navigate('/saint-of-the-day');
    } else {
      navigate(`/profile/${post.author_id}`);
    }
  };

  return (
    <Card className={`bg-white border-[#E5E0D5] shadow-sm hover:shadow-md transition-all duration-300 ${post.is_announcement ? 'border-l-4 border-l-[#D4AF37] ring-1 ring-[#D4AF37]/20' : ''} ${post.is_vatican_news ? 'border-l-4 border-l-[#D4AF37]' : ''} ${post.is_ewtn_news ? 'border-l-4 border-l-[#1B3A5C]' : ''} ${post.is_saint_of_day ? 'border-l-4 border-l-[#8B4513]' : ''}`} data-testid={`post-card-${post.post_id}`}>
      {post.is_repost && (
        <div className="px-4 pt-3 pb-0 flex items-center gap-1.5 text-xs text-green-600 font-['Nunito']" data-testid={`repost-indicator-${post.post_id}`}>
          <Repeat2 className="w-3.5 h-3.5" />
          <span className="font-medium">{isOwnPost ? 'You' : post.author_name} reposted</span>
        </div>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 border border-[#E5E0D5] cursor-pointer" onClick={handleAuthorClick}>
            <AvatarImage src={post.author_picture} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm font-['Nunito']">{post.author_name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {post.is_vatican_news && (
                <Badge className="bg-[#D4AF37] text-white text-[10px] font-['Nunito'] px-1.5 py-0 h-5">
                  <Globe className="w-2.5 h-2.5 mr-0.5" /> Vatican News
                </Badge>
              )}
              {post.is_ewtn_news && (
                <Badge className="bg-[#1B3A5C] text-white text-[10px] font-['Nunito'] px-1.5 py-0 h-5">
                  <Globe className="w-2.5 h-2.5 mr-0.5" /> EWTN News
                </Badge>
              )}
              {post.is_saint_of_day && (
                <Badge className="bg-[#8B4513] text-white text-[10px] font-['Nunito'] px-1.5 py-0 h-5" data-testid="saint-of-day-badge">
                  <Star className="w-2.5 h-2.5 mr-0.5" /> Saint of the Day
                </Badge>
              )}
              {post.is_announcement && !post.is_vatican_news && !post.is_ewtn_news && !post.is_saint_of_day && (
                <Badge className="bg-[var(--liturgical-secondary)] text-[#2D2A26] text-[10px] font-['Nunito'] px-1.5 py-0 h-5">
                  <Megaphone className="w-2.5 h-2.5 mr-0.5" /> Announcement
                </Badge>
              )}
              {!post.is_vatican_news && !post.is_ewtn_news && !post.is_saint_of_day && getPriorityBadge()}
              <span className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] cursor-pointer hover:underline" onClick={handleAuthorClick}>{post.author_name}</span>
              {post.is_parish_post && (
                <Badge className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] border-[var(--liturgical-primary)]/20 text-[10px] font-['Nunito']">
                  Parish Admin
                </Badge>
              )}
              {post.is_business_post && (
                <Badge className="bg-[#556B2F]/10 text-[#556B2F] border-[#556B2F]/20 text-[10px] font-['Nunito']" data-testid={`business-badge-${post.post_id}`}>
                  <Building2 className="w-2.5 h-2.5 mr-0.5" />{post.business_category || "Business"}
                </Badge>
              )}
              {post.author_religious_role && !post.is_parish_post && (
                <Badge 
                  className="text-[10px] font-['Nunito'] font-medium px-1.5 py-0"
                  style={{ 
                    backgroundColor: `${post.author_religious_role_color}15`,
                    color: post.author_religious_role_color,
                    borderColor: post.author_religious_role_color
                  }}
                >
                  {post.author_religious_role_name}
                </Badge>
              )}
              {post.parish_name && (
                <Badge variant="outline" className="border-[var(--liturgical-secondary)]/50 text-[var(--liturgical-secondary)] text-[10px] font-['Nunito']">{post.parish_name}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-[#6B665E] font-['Nunito']">{timeAgo(post.created_at)}</span>
              {post.is_edited && (
                <span className="text-xs text-[#9B9590] font-['Nunito'] italic" data-testid={`post-edited-${post.post_id}`}>(edited)</span>
              )}
              {post.scope === "global" ? <Globe className="w-3 h-3 text-[#6B665E]" /> : <Church className="w-3 h-3 text-[#6B665E]" />}
            </div>
          </div>
          
          {/* 3-dot menu for post actions - hide for news system accounts */}
          {!isNewsPost && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B665E] hover:text-[#2D2A26] hover:bg-[#F9F7F2]" data-testid={`post-menu-${post.post_id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white border-[#E5E0D5]">
              {isOwnPost && (
                <>
                  <DropdownMenuItem 
                    onClick={() => { setEditContent(post.content); setEditDialogOpen(true); }}
                    className="text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] text-sm cursor-pointer"
                    data-testid={`edit-post-${post.post_id}`}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Post
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-[#991B1B] hover:text-[#991B1B] hover:bg-red-50 font-['Nunito'] text-sm cursor-pointer"
                    data-testid={`delete-post-${post.post_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Post
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#E5E0D5]" />
                </>
              )}
              {!isOwnPost && !isNewsPost && (
                <>
                  <DropdownMenuItem 
                    onClick={handleMuteAuthor}
                    disabled={muting}
                    className="text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] text-sm cursor-pointer"
                    data-testid={`mute-author-${post.author_id}`}
                  >
                    <VolumeX className="w-4 h-4 mr-2" />
                    {muting ? "Muting..." : `Mute ${post.author_name?.split(' ')[0]}`}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleBlockUser}
                    disabled={blocking}
                    className="text-[#991B1B] hover:text-[#991B1B] hover:bg-red-50 font-['Nunito'] text-sm cursor-pointer"
                    data-testid={`block-user-${post.author_id}`}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    {blocking ? "Blocking..." : `Block ${post.author_name?.split(' ')[0]}`}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#E5E0D5]" />
                  <DropdownMenuItem 
                    onClick={() => setReportDialogOpen(true)}
                    className="text-[#991B1B] hover:text-[#991B1B] hover:bg-red-50 font-['Nunito'] text-sm cursor-pointer"
                    data-testid={`report-post-${post.post_id}`}
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Report Post
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {displayContent && (
          <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed whitespace-pre-wrap">
            <RenderContent content={displayContent} />
          </p>
        )}
        
        {/* Repost: Show original post content */}
        {post.is_repost && post.original_post && (
          <div className="border border-[#E5E0D5] rounded-lg overflow-hidden bg-[#F9F7F2]" data-testid={`repost-original-${post.post_id}`}>
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={post.original_post.author_picture} />
                  <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">
                    {post.original_post.author_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span 
                  className="text-xs font-semibold text-[#2D2A26] font-['Nunito'] cursor-pointer hover:underline"
                  onClick={() => navigate(`/profile/${post.original_post.author_id}`)}
                >
                  {post.original_post.author_name}
                </span>
                {post.original_post.created_at && (
                  <span className="text-[10px] text-[#6B665E] font-['Nunito']">
                    {(() => {
                      const d = new Date(post.original_post.created_at);
                      const now = new Date();
                      const diff = Math.floor((now - d) / 60000);
                      if (diff < 60) return `${diff}m ago`;
                      if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
                      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    })()}
                  </span>
                )}
              </div>
              {post.original_post.content && (
                <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed whitespace-pre-wrap">
                  <RenderContent content={post.original_post.content} />
                </p>
              )}
              {post.original_post.image_url && (
                <img 
                  src={resolveImageUrl(post.original_post.image_url)} 
                  alt="" 
                  className="w-full rounded-md border border-[#E5E0D5] max-h-64 object-contain bg-white"
                />
              )}
              {post.original_post.media_urls?.length > 0 && (
                <div className="grid gap-2" style={{ gridTemplateColumns: post.original_post.media_urls.length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
                  {post.original_post.media_urls.slice(0, 4).map((url, i) => (
                    <img key={i} src={resolveImageUrl(url)} alt="" className="w-full rounded-md border border-[#E5E0D5] max-h-48 object-cover" />
                  ))}
                </div>
              )}
              {post.original_post.cloudflare_video_id && (
                <CloudflareVideoPlayer videoId={post.original_post.cloudflare_video_id} className="max-h-64" />
              )}
            </div>
          </div>
        )}
        {post.image_url && (
          <>
            <img 
              src={resolveImageUrl(post.image_url)} 
              alt="" 
              className="w-full rounded-md border border-[#E5E0D5] max-h-96 object-contain bg-[#F9F7F2] cursor-pointer hover:opacity-95 transition-opacity" 
              onClick={() => setLightboxOpen(true)}
            />
            {/* Image Lightbox */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
              <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none">
                <button 
                  onClick={() => setLightboxOpen(false)}
                  className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="flex items-center justify-center w-full h-full min-h-[50vh]">
                  <img 
                    src={resolveImageUrl(post.image_url)} 
                    alt="" 
                    className="max-w-full max-h-[85vh] object-contain"
                  />
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
        {/* Cloudflare Stream Video */}
        {post.cloudflare_video_id && (
          <CloudflareVideoPlayer 
            videoId={post.cloudflare_video_id}
            className="max-h-96"
          />
        )}
        {/* Legacy local video */}
        {post.video_url && !post.cloudflare_video_id && (
          <VideoProcessingIndicator 
            videoId={post.video_id}
            videoUrl={post.video_url}
            onProcessingComplete={() => {}}
          />
        )}
        {/* YouTube Embeds - Auto-detected from post content */}
        {youtubeVideos.length > 0 && (
          <div className="space-y-3">
            {youtubeVideos.map((video) => (
              <div key={video.id} className={video.isShort ? 'flex justify-center' : ''}>
                <div className={video.isShort ? 'w-full max-w-[320px] sm:max-w-[300px]' : 'w-full'}>
                  <div 
                    className="relative rounded-lg overflow-hidden border border-[#E5E0D5] bg-black w-full"
                    style={video.isShort ? { aspectRatio: '9 / 16', maxHeight: '480px' } : { paddingBottom: '56.25%' }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?rel=0`}
                      className={`border-0 ${video.isShort ? 'w-full h-full' : 'absolute top-0 left-0 w-full h-full'}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="YouTube video"
                    />
                  </div>
                  <YouTubeTitle videoId={video.id} />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Instagram & TikTok Embeds */}
        {post.embeds && post.embeds.length > 0 && (
          <SocialEmbeds embeds={post.embeds} />
        )}
        {/* Poll */}
        {post.poll && (
          <PollCard 
            poll={post.poll} 
            postId={post.post_id}
            currentUserId={currentUserId}
            authorId={post.author_id}
            onVote={() => {}}
          />
        )}
        <div className="flex items-center gap-4 pt-2 border-t border-[#E5E0D5]/50">
          <div className="flex items-center">
            <button
              className={`flex items-center gap-1 text-sm font-['Nunito'] transition-colors ${post.user_liked ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-primary)]'}`}
              onClick={() => onLike(post.post_id)}
              data-testid={`post-like-btn-${post.post_id}`}
            >
              <Heart className={`w-4 h-4 ${post.user_liked ? 'fill-current' : ''}`} />
            </button>
            {(post.like_count || 0) > 0 && (
              <button
                className={`text-sm font-['Nunito'] ml-1 transition-colors hover:underline cursor-pointer ${post.user_liked ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-primary)]'}`}
                onClick={() => setLikesDialogOpen(true)}
                data-testid={`post-like-count-${post.post_id}`}
              >
                {post.like_count}
              </button>
            )}
            {(post.like_count || 0) === 0 && (
              <span className="text-sm font-['Nunito'] ml-1 text-[#6B665E]">0</span>
            )}
          </div>
          <button
            className="flex items-center gap-1.5 text-sm font-['Nunito'] text-[#6B665E] hover:text-[#2D2A26] transition-colors"
            onClick={loadComments}
            data-testid={`post-comment-btn-${post.post_id}`}
          >
            <MessageCircle className="w-4 h-4" />
            {post.comment_count || 0}
          </button>
          {/* Repost button - don't show for own posts, show count only */}
          {!isOwnPost ? (
            <button
              className="flex items-center gap-1.5 text-sm font-['Nunito'] text-[#6B665E] hover:text-green-600 transition-colors"
              onClick={() => setRepostDialogOpen(true)}
              data-testid={`post-repost-btn-${post.post_id}`}
              title="Repost"
            >
              <Repeat2 className="w-4 h-4" />
              {localRepostCount}
            </button>
          ) : (localRepostCount > 0) && (
            <span className="flex items-center gap-1.5 text-sm font-['Nunito'] text-[#6B665E]" data-testid={`post-repost-count-${post.post_id}`}>
              <Repeat2 className="w-4 h-4" />
              {localRepostCount}
            </span>
          )}
          {/* Share button */}
          <button
            className="flex items-center gap-1.5 text-sm font-['Nunito'] text-[#6B665E] hover:text-blue-600 transition-colors"
            onClick={() => setShareDialogOpen(true)}
            data-testid={`post-share-btn-${post.post_id}`}
            title="Share via message"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <div className="flex-1" />
          <button
            className={`flex items-center gap-1 text-sm font-['Nunito'] transition-colors ${bookmarked ? 'text-[var(--liturgical-secondary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-secondary)]'}`}
            onClick={openBookmarkDialog}
            data-testid={`post-bookmark-btn-${post.post_id}`}
          >
            <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-current' : ''}`} />
          </button>
        </div>
        
        {/* Edit Post Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                <Pencil className="w-5 h-5 text-[var(--liturgical-primary)]" /> Edit Post
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="What's on your mind?"
                className="min-h-[120px] border-[#E5E0D5] font-['Nunito'] resize-none focus-visible:ring-[var(--liturgical-primary)]"
                data-testid="edit-post-textarea"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-[#E5E0D5] font-['Nunito']">
                Cancel
              </Button>
              <Button onClick={handleEdit} disabled={editing} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="save-edit-btn">
                {editing ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Post Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-['Crimson_Text']">Delete Post?</AlertDialogTitle>
              <AlertDialogDescription className="font-['Nunito']">
                This action cannot be undone. Your post and all its comments will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="font-['Nunito']">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 font-['Nunito']"
                data-testid="confirm-delete-btn"
              >
                {deleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Bookmark Folder Dialog */}
        <Dialog open={bookmarkDialogOpen} onOpenChange={setBookmarkDialogOpen}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                <Bookmark className="w-5 h-5 text-[var(--liturgical-secondary)]" /> 
                {bookmarked ? "Move Bookmark" : "Save to Bookmarks"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {loadingFolders ? (
                <div className="text-center py-4 text-[#6B665E] font-['Nunito']">Loading folders...</div>
              ) : (
                <>
                  {/* Quick save without folder */}
                  <Button
                    variant="outline"
                    className="w-full justify-start border-[#E5E0D5] font-['Nunito'] text-sm h-10"
                    onClick={() => handleBookmark(null)}
                    data-testid="bookmark-no-folder-btn"
                  >
                    <Bookmark className="w-4 h-4 mr-2" />
                    {bookmarked ? "Remove Bookmark" : "Save without folder"}
                  </Button>
                  
                  {/* Existing folders */}
                  {folders.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase">Your Folders</p>
                      {folders.map(folder => (
                        <Button
                          key={folder.folder_id}
                          variant="outline"
                          className={`w-full justify-between border-[#E5E0D5] font-['Nunito'] text-sm h-10 ${selectedFolderId === folder.folder_id ? 'border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5' : ''}`}
                          onClick={() => handleBookmark(folder.folder_id)}
                          data-testid={`bookmark-folder-${folder.folder_id}`}
                        >
                          <span className="flex items-center">
                            <FolderPlus className="w-4 h-4 mr-2 text-[var(--liturgical-primary)]" />
                            {folder.name}
                          </span>
                          <span className="text-xs text-[#6B665E]">{folder.bookmark_count || 0}</span>
                        </Button>
                      ))}
                    </div>
                  )}
                  
                  {/* Create new folder */}
                  <div className="pt-2 border-t border-[#E5E0D5]">
                    <p className="text-xs text-[#6B665E] font-['Nunito'] font-semibold uppercase mb-2">Create New Folder</p>
                    <div className="flex gap-2">
                      <Input
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="Folder name"
                        className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                        data-testid="new-folder-input"
                      />
                      <Button
                        onClick={handleCreateFolder}
                        disabled={creatingFolder || !newFolderName.trim()}
                        size="sm"
                        className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                        data-testid="create-folder-btn"
                      >
                        {creatingFolder ? "..." : <Plus className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Report Post Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#991B1B]" /> Report Post
              </DialogTitle>
              <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
                Help us keep our community safe by reporting inappropriate content.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Why are you reporting this post?</label>
                <Select value={reportReason} onValueChange={setReportReason}>
                  <SelectTrigger className="border-[#E5E0D5]" data-testid="report-reason-select">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="spam" className="font-['Nunito']">Spam or misleading</SelectItem>
                    <SelectItem value="harassment" className="font-['Nunito']">Harassment or bullying</SelectItem>
                    <SelectItem value="hate_speech" className="font-['Nunito']">Hate speech or discrimination</SelectItem>
                    <SelectItem value="inappropriate" className="font-['Nunito']">Inappropriate content</SelectItem>
                    <SelectItem value="misinformation" className="font-['Nunito']">False information</SelectItem>
                    <SelectItem value="against_faith" className="font-['Nunito']">Against Catholic teaching</SelectItem>
                    <SelectItem value="other" className="font-['Nunito']">Other concern</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-[#F9F7F2] rounded-lg p-3 border border-[#E5E0D5]">
                <p className="text-xs text-[#6B665E] font-['Nunito']">
                  <strong>Note:</strong> Your report will be reviewed by our community moderators. False reports may result in action against your account.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReportDialogOpen(false)} className="border-[#E5E0D5] font-['Nunito']">
                Cancel
              </Button>
              <Button 
                onClick={handleReport} 
                disabled={!reportReason || reporting}
                className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-['Nunito']"
                data-testid="submit-report-btn"
              >
                {reporting ? "Submitting..." : "Submit Report"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {showComments && (
          <div className="space-y-1 pt-2" data-testid={`comments-section-${post.post_id}`}>
            {loadingComments ? (
              <div className="text-center text-xs text-[#6B665E]">Loading...</div>
            ) : (
              <>
                <CommentThread
                  comments={comments}
                  postId={post.post_id}
                  currentUserId={currentUserId}
                  onCommentsChange={setComments}
                />
                <CommentInput postId={post.post_id} onCommentAdded={(c) => setComments(prev => [...prev, c])} />
              </>
            )}
          </div>
        )}
        
        {/* Repost Dialog */}
        <Dialog open={repostDialogOpen} onOpenChange={setRepostDialogOpen}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                <Repeat2 className="w-5 h-5 text-green-600" /> Repost
              </DialogTitle>
              <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
                Share this post to your feed. You can add an optional comment.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Parish restriction notice */}
              {(post.scope === "parish" || post.is_parish_post) && post.parish_id && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="repost-parish-notice">
                  <Church className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-xs font-['Nunito'] text-amber-700">
                    This repost will be restricted to <strong>{post.parish_name || "the parish"}</strong> as the original post is parish-only.
                  </span>
                </div>
              )}
              {/* Original post preview */}
              <div className="bg-[#F9F7F2] rounded-lg p-3 border border-[#E5E0D5]">
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={post.author_picture} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">{post.author_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs font-semibold text-[#2D2A26]">{post.author_name}</span>
                </div>
                <p className="text-xs text-[#6B665E] line-clamp-3">{post.content}</p>
              </div>
              <Textarea
                value={repostComment}
                onChange={(e) => setRepostComment(e.target.value)}
                placeholder="Add your thoughts (optional)..."
                className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px]"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRepostDialogOpen(false)} className="border-[#E5E0D5] font-['Nunito']">
                Cancel
              </Button>
              <Button 
                onClick={handleRepost} 
                disabled={reposting}
                className="bg-green-600 hover:bg-green-700 text-white font-['Nunito']"
              >
                {reposting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reposting...</> : "Repost"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Share via Message Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={(open) => {
          setShareDialogOpen(open);
          if (!open) {
            setSelectedShareUser(null);
            setShareRecipient("");
            setShareSearchResults([]);
          }
        }}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-600" /> Share Post
              </DialogTitle>
              <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
                Send this post to another user via message.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* User search */}
              <div className="space-y-2">
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Search for a user</label>
                <Input
                  value={shareRecipient}
                  onChange={(e) => {
                    setShareRecipient(e.target.value);
                    searchUsersForShare(e.target.value);
                  }}
                  placeholder="Type a name..."
                  className="border-[#E5E0D5] font-['Nunito']"
                />
                
                {/* Search results */}
                {searchingUsers && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[var(--liturgical-primary)]" />
                  </div>
                )}
                {!searchingUsers && shareSearchResults.length > 0 && (
                  <div className="border border-[#E5E0D5] rounded-md max-h-[150px] overflow-y-auto">
                    {shareSearchResults.map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => {
                          setSelectedShareUser(u);
                          setShareRecipient(u.name);
                          setShareSearchResults([]);
                        }}
                        className="w-full flex items-center gap-2 p-2 hover:bg-[#F9F7F2] text-left"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.picture || u.profile_picture} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{u.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="font-['Nunito'] text-sm text-[#2D2A26]">{u.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Selected user */}
                {selectedShareUser && (
                  <div className="flex items-center gap-2 p-2 bg-[#F9F7F2] rounded-md">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={selectedShareUser.picture || selectedShareUser.profile_picture} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{selectedShareUser.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <span className="font-['Nunito'] text-sm text-[#2D2A26] flex-1">{selectedShareUser.name}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => {
                      setSelectedShareUser(null);
                      setShareRecipient("");
                    }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShareDialogOpen(false)} className="border-[#E5E0D5] font-['Nunito']">
                Cancel
              </Button>
              <Button 
                onClick={handleShareToUser} 
                disabled={sharing || !selectedShareUser}
                className="bg-blue-600 hover:bg-blue-700 text-white font-['Nunito']"
              >
                {sharing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <LikesDialog postId={post.post_id} open={likesDialogOpen} onOpenChange={setLikesDialogOpen} />
      </CardContent>
    </Card>
  );
}

function JoinParishDialog({ user }) {
  const [parishes, setParishes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getParishes().then(res => { 
      const data = res.data;
      setParishes(Array.isArray(data) ? data : (data.parishes || []));
      setLoading(false); 
    }).catch(() => setLoading(false));
  }, []);

  const handleJoin = async (parishId) => {
    try {
      await requestMembership(parishId);
      toast.success("Membership request sent! A parish admin will review your request.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="bg-[var(--liturgical-secondary)] text-[#2D2A26] hover:brightness-90 font-['Nunito'] font-semibold" data-testid="join-parish-btn">
          <Church className="w-4 h-4 mr-2" /> Join a Parish
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Find Your Parish</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="text-center py-8 text-[#6B665E]">Loading parishes...</div>
          ) : parishes.length === 0 ? (
            <div className="text-center py-8 text-[#6B665E]">No parishes available yet.</div>
          ) : (
            parishes.map((p) => (
              <div key={p.parish_id} className="flex items-center gap-4 p-4 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]" data-testid={`parish-option-${p.parish_id}`}>
                <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                  <img src={p.image_url || "https://images.unsplash.com/photo-1601652907493-a9775584c6e2?w=100"} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] text-sm">{p.name}</h4>
                  <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1"><MapPin className="w-3 h-3" />{p.address || "Location not available"}</p>
                </div>
                <Button size="sm" onClick={() => handleJoin(p.parish_id)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-xs" data-testid={`request-join-${p.parish_id}`}>
                  Request
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Sponsored Ad Card for Featured Listings
function SponsoredAdCard({ ad }) {
  const navigate = useNavigate();
  const listing = ad.listing;
  
  if (!listing) return null;
  
  const handleClick = () => {
    navigate(`/directory/${listing.listing_id}`);
  };
  
  return (
    <Card 
      className="bg-gradient-to-r from-[#FFFEF8] to-[#FFF9E6] border-[var(--liturgical-secondary)]/30 hover:border-[var(--liturgical-secondary)] transition-all cursor-pointer group relative overflow-hidden"
      onClick={handleClick}
      data-testid={`sponsored-ad-${listing.listing_id}`}
    >
      {/* Sponsored Badge */}
      <div className="absolute top-0 right-0 bg-gradient-to-l from-[#D4AF37] to-[#B8972F] text-white text-[10px] font-['Nunito'] px-3 py-1 rounded-bl-lg flex items-center gap-1">
        <Sparkles className="w-3 h-3" />
        Sponsored
      </div>
      
      <CardContent className="p-4 pt-5">
        <div className="flex gap-4">
          {/* Business Logo/Icon */}
          <div className="w-14 h-14 rounded-lg bg-[#F9F7F2] flex items-center justify-center flex-shrink-0 overflow-hidden border border-[#E5E0D5]">
            {listing.photos && listing.photos.length > 0 ? (
              <img 
                src={resolveImageUrl(listing.photos[0])} 
                alt={listing.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Building2 className="w-6 h-6 text-[#6B665E]" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Business Name & Category */}
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] group-hover:text-[var(--liturgical-primary)] transition-colors line-clamp-1">
                {listing.name}
              </h3>
            </div>
            
            <Badge className="bg-[#F0EBE0] text-[#6B665E] text-[10px] font-['Nunito'] mt-1">
              {listing.category}
            </Badge>
            
            {/* Description */}
            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-2 line-clamp-2">
              {listing.description}
            </p>
            
            {/* Footer Row */}
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#E5E0D5]/50">
              <div className="flex items-center gap-3">
                {/* Rating */}
                {listing.review_count > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-[var(--liturgical-secondary)] fill-[#D4AF37]" />
                    <span className="text-xs text-[#2D2A26] font-['Nunito'] font-medium">
                      {listing.average_rating?.toFixed(1)}
                    </span>
                    <span className="text-[10px] text-[#6B665E] font-['Nunito']">
                      ({listing.review_count})
                    </span>
                  </div>
                )}
                
                {/* Location */}
                <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {listing.service_area}
                </span>
              </div>
              
              {/* Quick Contact */}
              <div className="flex items-center gap-2">
                {listing.phone && (
                  <a 
                    href={`tel:${listing.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 p-1 rounded transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </a>
                )}
                {listing.email && (
                  <a 
                    href={`mailto:${listing.email}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 p-1 rounded transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </a>
                )}
                {listing.website && (
                  <a 
                    href={listing.website.startsWith('http') ? listing.website : `https://${listing.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 p-1 rounded transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Upcoming Events Widget for Dashboard Sidebar
function UpcomingEventsWidget({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await getEvents("parish");
        // Filter to only show FUTURE events from user's parishes and sort by date
        const userParishIds = [user.parish_id, ...(user.parishes?.map(p => p.parish_id) || [])].filter(Boolean);
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Start of today
        
        const filteredEvents = (res.data || [])
          .filter(e => {
            // Only include events from today onwards
            const eventDate = new Date(e.event_date);
            return eventDate >= now && (userParishIds.includes(e.parish_id) || e.scope === "global");
          })
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
          .slice(0, 5);
        setEvents(filteredEvents);
      } catch (err) {
        console.error("Failed to load events:", err);
      }
      setLoading(false);
    };
    loadEvents();
  }, [user]);

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === now.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else {
      return date.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };

  const formatEventTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Card className="bg-white border-[#E5E0D5] shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-4 h-4 text-[var(--liturgical-primary)]" />
            <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">Upcoming Events</h3>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-[#E5E0D5] rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-[#E5E0D5] rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card className="bg-white border-[#E5E0D5] shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-4 h-4 text-[var(--liturgical-primary)]" />
            <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">Upcoming Events</h3>
          </div>
          <p className="text-sm text-[#6B665E] font-['Nunito'] text-center py-4">
            No upcoming events
          </p>
          {user.parish_id && (
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-xs border-[#E5E0D5] font-['Nunito']"
              onClick={() => navigate(`/parish/${user.parish_id}`)}
            >
              View Parish
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E5E0D5] shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[var(--liturgical-primary)]" />
            <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">Upcoming Events</h3>
          </div>
        </div>
        <div className="space-y-3">
          {events.map(event => (
            <div 
              key={event.event_id}
              className="p-2.5 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]/50 cursor-pointer hover:bg-[#F0EBE0] hover:border-[var(--liturgical-primary)]/30 transition-all"
              onClick={() => navigate(`/parish/${event.parish_id}`)}
            >
              <div className="flex gap-3">
                <div className="w-11 h-11 bg-[var(--liturgical-primary)]/10 rounded-md flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-[9px] font-['Nunito'] text-[var(--liturgical-primary)] uppercase font-semibold leading-tight">
                    {new Date(event.event_date).toLocaleDateString('en', { month: 'short' })}
                  </span>
                  <span className="text-sm font-['Crimson_Text'] font-bold text-[var(--liturgical-primary)] leading-tight">
                    {new Date(event.event_date).getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-['Nunito'] font-semibold text-sm text-[#2D2A26] truncate">{event.title}</h4>
                  <div className="flex items-center gap-2 mt-1 text-xs text-[#6B665E] font-['Nunito']">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatEventTime(event.event_date)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-[#6B665E] font-['Nunito']">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{event.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {user.parish_id && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full mt-3 text-xs text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10 font-['Nunito']"
            onClick={() => navigate(`/parish/${user.parish_id}`)}
          >
            View All Events
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage({ user }) {
  const [posts, setPosts] = useState([]);
  const [sponsoredAds, setSponsoredAds] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [postScope, setPostScope] = useState("global");
  const [selectedParishId, setSelectedParishId] = useState("");
  const [feedTab, setFeedTab] = useState("all");
  const [feedParishFilter, setFeedParishFilter] = useState("all"); // For filtering "My Parish" feed - "all" means all parishes
  const [posting, setPosting] = useState(false);
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image' or 'video'
  const [imagePreview, setImagePreview] = useState(null); // Keep for backward compat, will be removed
  const [uploading, setUploading] = useState(false);
  const [parishCount, setParishCount] = useState(0);
  const [userParishes, setUserParishes] = useState([]);
  const fileInputRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  
  // Cloudflare Stream upload hook
  const { 
    uploadProgress: cfUploadProgress, 
    uploadError: cfUploadError, 
    isUploading: cfIsUploading,
    uploadStatus: cfUploadStatus,
    startUpload: startCfUpload,
    cancelUpload: cancelCfUpload 
  } = useCloudflareUpload();
  const [pendingVideoId, setPendingVideoId] = useState(null);
  
  // Poll state
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollDuration, setPollDuration] = useState("");
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  const [pollShowResultsBefore, setPollShowResultsBefore] = useState(false);
  const [pollAnonymous, setPollAnonymous] = useState(true);
  
  // Social media embeds (Instagram, TikTok)
  const [detectedEmbeds, setDetectedEmbeds] = useState([]);
  const [detectingEmbeds, setDetectingEmbeds] = useState(false);
  
  // Post as business
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [postAsBusiness, setPostAsBusiness] = useState(null); // null = personal, listing_id = business
  const [postAsAdmin, setPostAsAdmin] = useState(false); // Parish admins can choose to post as "Parish Admin"
  
  // Beta notice popup
  const [showBetaNotice, setShowBetaNotice] = useState(false);
  
  // Show beta notice only once per user - check both localStorage and sessionStorage
  useEffect(() => {
    // Use a combination of localStorage (persists across sessions) and check for just-logged-in state
    const hasSeenBetaNotice = localStorage.getItem('parish_beta_notice_seen');
    const justLoggedIn = sessionStorage.getItem('parish_just_logged_in');
    
    // Only show if: never seen AND just logged in (this session)
    // This ensures it only shows right after login, not on every page refresh
    if (!hasSeenBetaNotice && justLoggedIn) {
      // Clear the just-logged-in flag so it doesn't show again on refresh
      sessionStorage.removeItem('parish_just_logged_in');
      // Small delay so the dashboard loads first
      const timer = setTimeout(() => setShowBetaNotice(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const dismissBetaNotice = () => {
    localStorage.setItem('parish_beta_notice_seen', 'true');
    setShowBetaNotice(false);
  };

  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [postsSkip, setPostsSkip] = useState(0);
  const POSTS_PER_PAGE = 50;

  const loadPosts = async (scope = "all", parishFilter = "") => {
    try {
      const res = await getPosts({ scope, parish_id: parishFilter || undefined, skip: 0, limit: POSTS_PER_PAGE });
      if (res.data.posts) {
        setPosts(res.data.posts);
        setSponsoredAds(res.data.sponsored_ads || []);
        setHasMorePosts(res.data.has_more || false);
        setPostsSkip(POSTS_PER_PAGE);
      } else {
        setPosts(res.data);
        setSponsoredAds([]);
        setHasMorePosts(false);
      }
    } catch {}
  };

  const loadMorePosts = async () => {
    if (loadingMore || !hasMorePosts) return;
    setLoadingMore(true);
    try {
      const scope = feedScope;
      const parishFilter = feedParishFilter;
      const res = await getPosts({ scope, parish_id: parishFilter !== "all" ? parishFilter : undefined, skip: postsSkip, limit: POSTS_PER_PAGE });
      if (res.data.posts) {
        setPosts(prev => [...prev, ...res.data.posts]);
        setHasMorePosts(res.data.has_more || false);
        setPostsSkip(prev => prev + POSTS_PER_PAGE);
      }
    } catch {}
    setLoadingMore(false);
  };

  // Load parish count and list on mount
  useEffect(() => {
    getUserParishCount()
      .then(res => {
        setParishCount(res.data.parish_count || 0);
        setUserParishes(res.data.parishes || []);
        // Set default selected parish if user has parishes
        if (res.data.parishes && res.data.parishes.length > 0) {
          setSelectedParishId(res.data.parishes[0].parish_id);
          // Keep feedParishFilter as "all" to show all parishes by default
        }
      })
      .catch(() => {
        setParishCount(user.parish_id ? 1 : 0);
        if (user.parish_id) {
          setUserParishes([{ parish_id: user.parish_id, parish_name: user.parish_name || "My Parish" }]);
          setSelectedParishId(user.parish_id);
        }
      });
  }, [user.parish_id, user.parish_name]);

  // Load user's businesses for "Post As" feature
  useEffect(() => {
    getMyBusinessListings()
      .then(res => setUserBusinesses(res.data.listings || []))
      .catch(() => {});
  }, []);

  useEffect(() => { 
    // When feedParishFilter is "all", don't pass a parish_id to get all parishes
    const parishIdFilter = feedParishFilter === "all" ? "" : feedParishFilter;
    loadPosts(feedTab, feedTab === "parish" ? parishIdFilter : ""); 
  }, [feedTab, feedParishFilter]);

  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    if (!isVideo && !isImage) {
      toast.error("Please select an image or video file");
      return;
    }
    
    // Size limits: 10MB for images, 1GB for videos
    const maxSize = isVideo ? 1024 * 1024 * 1024 : 10 * 1024 * 1024;
    const sizeLabel = isVideo ? "1GB" : "10MB";
    
    if (file.size > maxSize) {
      toast.error(`File must be under ${sizeLabel}`);
      return;
    }
    
    setMediaFile(file);
    setMediaType(isVideo ? 'video' : 'image');
    setMediaPreview(URL.createObjectURL(file));
    setImagePreview(URL.createObjectURL(file)); // Legacy support
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Poll helper functions
  const addPollOption = () => {
    if (pollOptions.length < 10) {
      setPollOptions([...pollOptions, ""]);
    }
  };

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const resetPoll = () => {
    setShowPollCreator(false);
    setPollQuestion("");
    setPollOptions(["", ""]);
    setPollDuration("");
    setPollAllowMultiple(false);
    setPollShowResultsBefore(false);
    setPollAnonymous(true);
  };

  const handleEmojiClick = (emojiObject) => {
    if (emojiObject.type === "catholic") {
      // For Catholic emojis, insert the special marker
      setNewPost(prev => prev + emojiObject.emoji);
    } else {
      // Standard emoji
      setNewPost(prev => prev + emojiObject.emoji);
    }
    setShowEmojiPicker(false);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect Instagram/TikTok URLs in post content (debounced)
  useEffect(() => {
    // URL patterns for Instagram and TikTok
    const instagramPattern = /https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/[A-Za-z0-9_-]+/gi;
    const tiktokPattern = /https?:\/\/(?:www\.|vm\.)?tiktok\.com\/(?:@[\w.]+\/video\/\d+|[A-Za-z0-9]+|t\/[A-Za-z0-9]+)/gi;
    
    const urls = [
      ...(newPost.match(instagramPattern) || []),
      ...(newPost.match(tiktokPattern) || [])
    ];
    
    // Only fetch embeds if we have URLs and they changed
    if (urls.length === 0) {
      setDetectedEmbeds([]);
      return;
    }
    
    // Check if URLs are different from current embeds
    const currentUrls = detectedEmbeds.map(e => e.url);
    const newUrls = urls.filter(u => !currentUrls.includes(u));
    
    if (newUrls.length === 0) return;
    
    // Debounce the API call
    const timer = setTimeout(async () => {
      setDetectingEmbeds(true);
      try {
        const res = await detectEmbeds(newPost);
        if (res.data.embeds && res.data.embeds.length > 0) {
          setDetectedEmbeds(res.data.embeds);
        }
      } catch (err) {
        console.error("Failed to detect embeds:", err);
      }
      setDetectingEmbeds(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, [newPost]);

  const handlePost = async () => {
    // Validate poll if creating one
    if (showPollCreator) {
      if (!pollQuestion.trim()) {
        toast.error("Please enter a poll question");
        return;
      }
      const validOptions = pollOptions.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        toast.error("Please add at least 2 options");
        return;
      }
      if (!pollDuration || parseInt(pollDuration) < 1) {
        toast.error("Please specify how many days the poll should run");
        return;
      }
    }
    
    if (!newPost.trim() && !mediaFile && !showPollCreator) return;
    setPosting(true);
    let mediaUrl = null;
    let uploadedMediaType = null;
    let cloudflareVideoId = null;
    
    if (mediaFile) {
      setUploading(true);
      
      // Use Cloudflare Stream for videos, local upload for images
      if (mediaType === 'video') {
        try {
          // Show persistent toast for mobile uploads with dynamic status
          const uploadToastId = toast.loading(
            cfUploadStatus || "Uploading video... Keep this screen open.", 
            { duration: Infinity }
          );
          
          // Update toast as status changes
          const updateToast = (status) => {
            if (status) {
              toast.loading(status, { id: uploadToastId });
            }
          };
          
          // Start Cloudflare Stream upload
          cloudflareVideoId = await startCfUpload(
            mediaFile,
            (result) => {
              toast.dismiss(uploadToastId);
              toast.success("Video uploaded! Processing in background...");
            },
            (error) => {
              toast.dismiss(uploadToastId);
              toast.error("Video upload failed: " + error.message);
            }
          );
          uploadedMediaType = 'video';
          toast.dismiss(uploadToastId);
        } catch (err) {
          console.error("Video upload error:", err);
          const errorMessage = err.response?.data?.detail || err.message || "Unknown error";
          toast.error("Failed to upload video: " + errorMessage);
          setPosting(false);
          setUploading(false);
          return;
        }
      } else {
        // Images go to local/Cloudinary storage
        try {
          const uploadRes = await uploadImage(mediaFile);
          mediaUrl = uploadRes.data.url;
          uploadedMediaType = uploadRes.data.type || mediaType;
        } catch (err) {
          toast.error("Failed to upload image");
          setPosting(false);
          setUploading(false);
          return;
        }
      }
      setUploading(false);
    }
    
    try {
      const postData = { 
        content: newPost || (showPollCreator ? pollQuestion : ""), 
        scope: postScope, 
        is_announcement: isAnnouncement 
      };
      
      // Add media to post
      if (cloudflareVideoId) {
        // Cloudflare Stream video
        postData.cloudflare_video_id = cloudflareVideoId;
      } else if (mediaUrl) {
        // Image or legacy video
        if (uploadedMediaType === 'video') {
          postData.video_url = mediaUrl;
        } else {
          postData.image_url = mediaUrl;
        }
      }
      
      // Include parish_id when posting to a specific parish
      if (postScope === "parish" && selectedParishId) {
        postData.parish_id = selectedParishId;
      }
      // Add poll data if creating a poll
      if (showPollCreator) {
        const validOptions = pollOptions.filter(opt => opt.trim());
        postData.poll = {
          question: pollQuestion.trim(),
          options: validOptions,
          duration_days: parseInt(pollDuration),
          allow_multiple: pollAllowMultiple,
          show_results_before_vote: pollShowResultsBefore,
          anonymous: pollAnonymous
        };
      }
      
      // Add social media embeds if detected
      if (detectedEmbeds.length > 0) {
        postData.embeds = detectedEmbeds;
      }
      
      // Add business listing ID if posting as a business
      if (postAsBusiness) {
        postData.business_listing_id = postAsBusiness;
      }
      
      // Add post as admin flag
      if (postAsAdmin && !postAsBusiness) {
        postData.post_as_admin = true;
      }
      
      const res = await createPost(postData);
      setPosts([res.data, ...posts]);
      setNewPost("");
      removeMedia();
      setIsAnnouncement(false);
      setPostAsBusiness(null);
      setPostAsAdmin(false);
      resetPoll();
      setDetectedEmbeds([]); // Clear detected embeds
      toast.success(showPollCreator ? "Poll posted!" : isAnnouncement ? "Announcement posted!" : cloudflareVideoId ? "Post shared! Video is processing..." : "Post shared!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create post");
    }
    setPosting(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await likePost(postId);
      setPosts(posts.map(p => p.post_id === postId ? {
        ...p,
        user_liked: res.data.liked,
        like_count: res.data.like_count
      } : p));
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="dashboard-page">
      {/* Beta Notice Dialog */}
      <Dialog open={showBetaNotice} onOpenChange={setShowBetaNotice}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--liturgical-primary)] to-purple-600 flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="font-['Crimson_Text'] text-xl">Welcome to Parish Beta!</DialogTitle>
                <Badge variant="outline" className="mt-1 text-xs border-[var(--liturgical-primary)] text-[var(--liturgical-primary)]">
                  Beta Version
                </Badge>
              </div>
            </div>
            <DialogDescription className="text-left pt-2 space-y-3 font-['Nunito']" asChild>
              <div>
                <div>
                  Thank you for being an early member of our community! Parish is currently in <strong>beta testing</strong>, 
                  which means you may encounter some bugs or features still in development.
                </div>
                <div>
                  Your feedback is invaluable in helping us improve. If you have suggestions, find issues, 
                  or just want to share your thoughts, please use the <strong>Support</strong> link in the top menu.
                </div>
                <div className="flex items-center gap-2 p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
                  <MessageSquareHeart className="w-5 h-5 text-[var(--liturgical-primary)] flex-shrink-0" />
                  <span className="text-sm">Click the <strong>?</strong> icon in the header to submit feedback anytime.</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button 
              onClick={dismissBetaNotice}
              className="w-full bg-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/90"
              data-testid="beta-notice-dismiss"
            >
              Got it, let's explore!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-6 items-start">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl space-y-8">
            {/* Parish Notice */}
            {!user.parish_id && (
              <div className="bg-white border border-[var(--liturgical-secondary)]/30 rounded-md p-5 flex items-center justify-between" data-testid="no-parish-notice">
                <div>
                  <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">Join a Parish</h3>
                  <p className="text-sm text-[#6B665E] font-['Nunito']">Connect with your local parish community</p>
                </div>
                <JoinParishDialog user={user} />
              </div>
            )}

        {/* Create Post */}
        <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="create-post-card">
          <CardContent className="pt-5 space-y-3">
            <div className="flex gap-3">
              <Avatar className="h-10 w-10 border border-[#E5E0D5]">
                {postAsBusiness ? (
                  <>
                    <AvatarImage src={userBusinesses.find(b => b.listing_id === postAsBusiness)?.photos?.[0]} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm font-['Nunito']"><Building2 className="w-5 h-5" /></AvatarFallback>
                  </>
                ) : (
                  <>
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm font-['Nunito']">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </>
                )}
              </Avatar>
              <div className="flex-1 space-y-2">
                {/* Post as selector - shown if user has businesses or is parish admin */}
                {(userBusinesses.length > 0 || user.is_parish_admin || user.is_site_admin) && (
                  <Select value={postAsAdmin ? "parish_admin" : postAsBusiness || "personal"} onValueChange={v => {
                    if (v === "parish_admin") { setPostAsAdmin(true); setPostAsBusiness(null); }
                    else if (v === "personal") { setPostAsAdmin(false); setPostAsBusiness(null); }
                    else { setPostAsAdmin(false); setPostAsBusiness(v); }
                  }}>
                    <SelectTrigger className="w-auto h-7 border-[#E5E0D5] text-xs font-['Nunito'] px-2 inline-flex gap-1" data-testid="post-as-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E0D5]">
                      <SelectItem value="personal" className="text-xs font-['Nunito']">
                        <span className="flex items-center gap-1.5">{user.name || "Personal"}</span>
                      </SelectItem>
                      {(user.is_parish_admin || user.is_site_admin) && postScope === "parish" && (
                        <SelectItem value="parish_admin" className="text-xs font-['Nunito']">
                          <span className="flex items-center gap-1.5"><Church className="w-3 h-3" />Parish Admin</span>
                        </SelectItem>
                      )}
                      {userBusinesses.map(b => (
                        <SelectItem key={b.listing_id} value={b.listing_id} className="text-xs font-['Nunito']">
                          <span className="flex items-center gap-1.5"><Building2 className="w-3 h-3" />{b.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Textarea
                  placeholder={isAnnouncement ? "Write a parish announcement..." : "Share with your community..."}
                  value={newPost}
                  onChange={(e) => {
                    setNewPost(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                  className="min-h-[80px] max-h-[400px] border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito'] text-sm resize-none overflow-y-auto"
                  data-testid="create-post-textarea"
                />
                {mediaPreview && (
                  <div className="relative inline-block">
                    {mediaType === 'video' ? (
                      <video src={mediaPreview} className="max-h-40 rounded-md border border-[#E5E0D5]" controls />
                    ) : (
                      <img src={mediaPreview} alt="Preview" className="max-h-40 rounded-md border border-[#E5E0D5]" />
                    )}
                    <button onClick={removeMedia} className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--liturgical-primary)] text-white rounded-full flex items-center justify-center hover:brightness-90" data-testid="remove-media-btn">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {/* Video Upload Progress Indicator */}
                {uploading && mediaType === 'video' && (
                  <div className="w-full p-4 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]" data-testid="video-upload-progress">
                    <div className="flex items-center gap-3 mb-2">
                      <Loader2 className="w-5 h-5 animate-spin text-[var(--liturgical-primary)]" />
                      <span className="text-sm font-medium text-[#2D2A26] font-['Nunito']">
                        {Object.values(cfUploadProgress)[0]?.statusText || 'Uploading video...'}
                      </span>
                    </div>
                    {Object.values(cfUploadProgress)[0]?.percentage > 0 && (
                      <div className="w-full bg-[#E5E0D5] rounded-full h-2">
                        <div 
                          className="bg-[var(--liturgical-primary)] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Object.values(cfUploadProgress)[0]?.percentage || 0}%` }}
                        />
                      </div>
                    )}
                    {Object.values(cfUploadProgress)[0]?.percentage > 0 && (
                      <p className="text-xs text-[#6B665E] mt-1 font-['Nunito']">
                        {Object.values(cfUploadProgress)[0]?.percentage}% complete
                      </p>
                    )}
                  </div>
                )}
                {/* Social Media Embed Preview */}
                {(detectingEmbeds || detectedEmbeds.length > 0) && (
                  <div className="space-y-2 p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]" data-testid="embed-preview-container">
                    {detectingEmbeds ? (
                      <div className="flex items-center gap-2 text-xs text-[#6B665E] font-['Nunito']">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Detecting social media link...
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#6B665E] font-['Nunito'] font-medium">
                            {detectedEmbeds.length === 1 ? "Social media embed detected" : `${detectedEmbeds.length} embeds detected`}
                          </span>
                          <button 
                            onClick={() => setDetectedEmbeds([])}
                            className="text-xs text-[#6B665E] hover:text-[#2D2A26] font-['Nunito']"
                            data-testid="clear-embeds-btn"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          {detectedEmbeds.map((embed, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs font-['Nunito'] bg-white p-2 rounded border border-[#E5E0D5]">
                              {embed.platform === 'instagram' ? (
                                <div className="w-5 h-5 rounded bg-gradient-to-tr from-[#405DE6] via-[#C13584] to-[#F77737] flex items-center justify-center">
                                  <span className="text-white text-[10px] font-bold">IG</span>
                                </div>
                              ) : (
                                <div className="w-5 h-5 rounded bg-black flex items-center justify-center">
                                  <span className="text-white text-[10px] font-bold">TT</span>
                                </div>
                              )}
                              <span className="text-[#2D2A26] flex-1 truncate">
                                {embed.author_name ? `@${embed.author_name}` : embed.platform === 'instagram' ? 'Instagram' : 'TikTok'} video
                              </span>
                              {embed.thumbnail_url && (
                                <img src={embed.thumbnail_url} alt="" className="w-8 h-8 rounded object-cover" />
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#6B665E] font-['Nunito']">Share with</span>
                <Select value={postScope} onValueChange={(value) => {
                  setPostScope(value);
                  // Reset to first parish when switching to parish scope
                  if (value === "parish" && userParishes.length > 0) {
                    setSelectedParishId(userParishes[0].parish_id);
                  }
                }}>
                  <SelectTrigger className="w-36 h-8 border-[#E5E0D5] text-xs font-['Nunito']" data-testid="post-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E5E0D5]">
                    <SelectItem value="global" className="text-xs font-['Nunito']"><span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Everyone</span></SelectItem>
                    {parishCount > 0 && <SelectItem value="parish" className="text-xs font-['Nunito']"><span className="flex items-center gap-1.5"><Church className="w-3 h-3" /> Parish Only</span></SelectItem>}
                  </SelectContent>
                </Select>
                {/* Parish selector - shown when posting to parish and user has multiple parishes */}
                {postScope === "parish" && userParishes.length > 1 && (
                  <Select value={selectedParishId} onValueChange={setSelectedParishId}>
                    <SelectTrigger className="w-48 h-8 border-[#E5E0D5] text-xs font-['Nunito']" data-testid="parish-select">
                      <SelectValue placeholder="Select parish" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E0D5]">
                      {userParishes.map((p) => (
                        <SelectItem key={p.parish_id} value={p.parish_id} className="text-xs font-['Nunito']">
                          {p.parish_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <input type="file" ref={fileInputRef} accept="image/*,video/*" onChange={handleMediaSelect} className="hidden" />
                <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={showPollCreator} className="h-8 px-2 text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5 disabled:opacity-50" data-testid="add-media-btn">
                  <ImagePlus className="w-4 h-4" />
                </Button>
                <div className="relative" ref={emojiPickerRef}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
                    className="h-8 px-2 text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5" 
                    data-testid="emoji-picker-btn"
                  >
                    <Smile className="w-4 h-4" />
                  </Button>
                  {showEmojiPicker && (
                    <div className="absolute top-10 right-0 sm:left-0 sm:right-auto z-50">
                      <CatholicEmojiPicker 
                        onEmojiClick={handleEmojiClick} 
                        width={Math.min(320, window.innerWidth - 32)} 
                        height={380}
                      />
                    </div>
                  )}
                </div>
                <Button 
                  variant={showPollCreator ? "default" : "ghost"} 
                  size="sm" 
                  onClick={() => { setShowPollCreator(!showPollCreator); if (showPollCreator) resetPoll(); else removeMedia(); }}
                  className={`h-8 px-2 ${showPollCreator ? 'bg-[var(--liturgical-primary)] text-white hover:brightness-90' : 'text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5'}`}
                  data-testid="add-poll-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                </Button>
                {user.role === "parish_admin" && parishCount > 0 && (
                  <Button
                    variant={isAnnouncement ? "default" : "ghost"}
                    size="sm"
                    onClick={() => { setIsAnnouncement(!isAnnouncement); if (!isAnnouncement) setPostScope("parish"); }}
                    className={`h-8 px-2.5 text-xs font-['Nunito'] ${isAnnouncement ? 'bg-[var(--liturgical-secondary)] text-[#2D2A26] hover:brightness-90' : 'text-[var(--liturgical-secondary)] hover:bg-[var(--liturgical-secondary)]/10'}`}
                    data-testid="announcement-toggle-btn"
                  >
                    <Megaphone className="w-3.5 h-3.5 mr-1" />
                    {isAnnouncement ? "Announcement" : "Pin"}
                  </Button>
                )}
              </div>
              <Button
                onClick={handlePost}
                disabled={posting || uploading || (!newPost.trim() && !mediaFile && !showPollCreator)}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold text-sm h-8 px-5 active:scale-95 transition-all"
                data-testid="create-post-btn"
              >
                {uploading ? "Uploading..." : posting ? "Posting..." : showPollCreator ? "Post Poll" : "Share"}
              </Button>
            </div>
            
            {/* Poll Creator */}
            {showPollCreator && (
              <div className="bg-[#F9F7F2] rounded-lg border border-[#E5E0D5] p-4 space-y-4" data-testid="poll-creator">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    <h4 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">Create Poll</h4>
                  </div>
                  <Button variant="ghost" size="sm" onClick={resetPoll} className="h-7 w-7 p-0 text-[#6B665E]">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Poll Question */}
                <div className="space-y-2">
                  <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Question *</Label>
                  <Input
                    value={pollQuestion}
                    onChange={(e) => setPollQuestion(e.target.value)}
                    placeholder="What would you like to ask?"
                    className="border-[#E5E0D5] font-['Nunito'] focus-visible:ring-[var(--liturgical-primary)]"
                    data-testid="poll-question-input"
                  />
                </div>
                
                {/* Poll Options */}
                <div className="space-y-2">
                  <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Options * (2-10)</Label>
                  <div className="space-y-2">
                    {pollOptions.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={option}
                          onChange={(e) => updatePollOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)]"
                          data-testid={`poll-option-input-${index}`}
                        />
                        {pollOptions.length > 2 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removePollOption(index)}
                            className="h-8 w-8 p-0 text-[#6B665E] hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  {pollOptions.length < 10 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addPollOption}
                      className="mt-2 border-dashed border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-xs"
                      data-testid="add-poll-option-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Option
                    </Button>
                  )}
                </div>
                
                {/* Poll Duration */}
                <div className="space-y-2">
                  <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Poll Duration (days) *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={pollDuration}
                    onChange={(e) => setPollDuration(e.target.value)}
                    placeholder="Enter number of days"
                    className="w-40 border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)]"
                    data-testid="poll-duration-input"
                  />
                </div>
                
                {/* Poll Settings */}
                <div className="space-y-3 pt-2 border-t border-[#E5E0D5]">
                  <h5 className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">Poll Settings</h5>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Allow Multiple Choices</Label>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">Let voters select more than one option</p>
                    </div>
                    <Switch
                      checked={pollAllowMultiple}
                      onCheckedChange={setPollAllowMultiple}
                      data-testid="poll-allow-multiple-switch"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Show Results Before Voting</Label>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">Display results to users who haven't voted yet</p>
                    </div>
                    <Switch
                      checked={pollShowResultsBefore}
                      onCheckedChange={setPollShowResultsBefore}
                      data-testid="poll-show-results-switch"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Anonymous Voting</Label>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">Hide who voted for each option</p>
                    </div>
                    <Switch
                      checked={pollAnonymous}
                      onCheckedChange={setPollAnonymous}
                      data-testid="poll-anonymous-switch"
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feed Tabs */}
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={feedTab} onValueChange={setFeedTab} data-testid="feed-tabs">
            <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
              <TabsTrigger value="all" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="feed-tab-all">
                All Posts
              </TabsTrigger>
              {parishCount > 0 && (
                <TabsTrigger value="parish" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]" data-testid="feed-tab-parish">
                  <Church className="w-3.5 h-3.5 mr-1.5" /> {parishCount > 1 ? "My Parishes" : "My Parish"}
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          
          {/* Parish dropdown for multi-parish users when viewing "My Parishes" */}
          {feedTab === "parish" && parishCount > 1 && (
            <Select value={feedParishFilter} onValueChange={setFeedParishFilter}>
              <SelectTrigger className="w-52 h-9 border-[#E5E0D5] text-xs font-['Nunito']" data-testid="feed-parish-filter">
                <SelectValue placeholder="All my parishes" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                <SelectItem value="all" className="text-xs font-['Nunito']">All my parishes</SelectItem>
                {userParishes.map((p) => (
                  <SelectItem key={p.parish_id} value={p.parish_id} className="text-xs font-['Nunito']">
                    {p.parish_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Posts Feed with Sponsored Ads */}
        <div className="space-y-6">
          {posts.length === 0 && sponsoredAds.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-feed">
              <Church className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
              <p className="text-[#6B665E] font-['Nunito']">No posts yet. Be the first to share!</p>
            </div>
          ) : (
            <>
              {/* Intersperse posts with sponsored ads */}
              {posts.map((post, index) => (
                <div key={post.post_id}>
                  <PostCard 
                    post={post} 
                    onLike={handleLike} 
                    onComment={() => loadPosts(feedTab)}
                    currentUserId={user?.user_id}
                    onMuteAuthor={(authorId) => {
                      // Remove posts from muted author from the current feed
                      setPosts(posts.filter(p => p.author_id !== authorId));
                    }}
                    onBlockUser={(authorId) => {
                      // Remove all posts from blocked user immediately
                      setPosts(posts.filter(p => p.author_id !== authorId));
                    }}
                    onEdit={async (postId, content) => {
                      await updatePost(postId, { content });
                      // Update the post in state
                      setPosts(posts.map(p => p.post_id === postId ? { ...p, content, is_edited: true, edited_at: new Date().toISOString() } : p));
                    }}
                    onDelete={async (postId) => {
                      await deletePost(postId);
                      // Remove the post from state
                      setPosts(posts.filter(p => p.post_id !== postId));
                    }}
                  />
                  
                  {/* Show a sponsored ad after every 4 posts (at indices 3, 7, 11...) */}
                  {sponsoredAds.length > 0 && (index + 1) % 4 === 0 && (
                    <div className="mt-6">
                      <SponsoredAdCard 
                        ad={sponsoredAds[Math.floor((index + 1) / 4 - 1) % sponsoredAds.length]} 
                      />
                    </div>
                  )}
                </div>
              ))}
              
              {/* If there are fewer than 4 posts but we have ads, show one at the end */}
              {posts.length > 0 && posts.length < 4 && sponsoredAds.length > 0 && (
                <SponsoredAdCard ad={sponsoredAds[0]} />
              )}
              
              {/* If no posts but we have ads, show them */}
              {posts.length === 0 && sponsoredAds.length > 0 && (
                <div className="space-y-6">
                  {sponsoredAds.slice(0, 3).map((ad, i) => (
                    <SponsoredAdCard key={ad.featured_id || i} ad={ad} />
                  ))}
                </div>
              )}
              
              {/* Load More */}
              {hasMorePosts && posts.length > 0 && (
                <div className="text-center py-6">
                  <Button variant="outline" onClick={loadMorePosts} disabled={loadingMore}
                    className="border-[#E5E0D5] font-['Nunito'] text-sm text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                    data-testid="load-more-posts">
                    {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {loadingMore ? "Loading..." : "Load More Posts"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
          </div>
          
          {/* Right Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0 space-y-4">
            {/* Upcoming Events Widget */}
            <UpcomingEventsWidget user={user} />
            
            {/* Friend Suggestions Widget */}
            <FriendSuggestionsWidget limit={5} />
          </div>
        </div>
      </div>
    </div>
  );
}
